import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { testimonialsPageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

export default function TestimonialDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  let story: typeof testimonialsPageData.categories[0]["stories"][0] | null = null;
  let categoryLabel = "";
  let categoryAccent = "";
  let categoryIcon = "";

  for (const cat of testimonialsPageData.categories) {
    const found = cat.stories.find((s) => s.id === id);
    if (found) {
      story = found;
      categoryLabel = cat.label;
      categoryAccent = cat.accent;
      categoryIcon = cat.icon;
      break;
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [id]);

  if (!story) {
    return (
      <>
        <SEO
          title="見證不存在｜時光逆行駭客"
          description="找不到您要的見證，請返回客戶見證列表瀏覽其他內容。"
          canonicalPath={`/testimonials/${id}`}
          noindex
        />
      <div className="min-h-screen bg-abyss-900 circuit-bg flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-ghost font-bold text-2xl mb-4">故事不存在</h2>
          <Link to="/testimonials" className="btn-gold-outline">
            返回客戶見證
          </Link>
        </div>
      </div>
    </>
    );
  }

  // Collect all stories for prev/next navigation
  const allStories: { id: string; title: string; categoryAccent: string }[] = [];
  for (const cat of testimonialsPageData.categories) {
    for (const s of cat.stories) {
      allStories.push({ id: s.id, title: s.title, categoryAccent: cat.accent });
    }
  }
  const currentIndex = allStories.findIndex((s) => s.id === id);
  const prevStory = currentIndex > 0 ? allStories[currentIndex - 1] : null;
  const nextStory = currentIndex < allStories.length - 1 ? allStories[currentIndex + 1] : null;

  // Related testimonials: same category, excluding current
  const relatedTestimonials: { id: string; title: string; identity: string; concern: string; categoryLabel: string; categoryAccent: string }[] = [];
  for (const cat of testimonialsPageData.categories) {
    if (cat.accent === categoryAccent) {
      for (const s of cat.stories) {
        if (s.id !== id) {
          relatedTestimonials.push({
            id: s.id,
            title: `${s.identity}｜${s.concern}`,
            identity: s.identity,
            concern: s.concern,
            categoryLabel: cat.label,
            categoryAccent: cat.accent,
          });
        }
      }
    }
  }
  const displayedRelated = relatedTestimonials.slice(0, 3);

  return (
    <>
      <SEO
        title={`${story.identity}的見證｜客戶見證｜時光逆行駭客`}
        description={`${story.identity}｜${story.concern}。${story.before.substring(0, 120)}`}
        keywords={`客戶見證,${categoryLabel},逆齡經驗,曾驛翔,時光逆行駭客`}
        canonicalPath={`/testimonials/${id}`}
        ogType="article"
        author="曾驛翔院長"
        schema={{
          "@context": "https://schema.org",
          "@type": "Review",
          "itemReviewed": {
            "@type": "Service",
            "name": "時光逆行駭客逆齡評估",
            "provider": {
              "@type": "Person",
              "name": "曾驛翔",
            },
          },
          "author": {
            "@type": "Person",
            "name": story.identity,
          },
          "reviewBody": `${story.before.substring(0, 80)}`,
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg">
      {/* Hero Banner */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={`https://readdy.ai/api/search-image?query=elegant%20dark%20abstract%20background%20with%20soft%20flowing%20light%20patterns%20in%20gold%20and%20cyan%20on%20deep%20navy%20black%20background%2C%20futuristic%20health%20wellness%20atmosphere%2C%20minimalist%20with%20subtle%20glow%2C%20no%20text%20no%20human&width=1920&height=500&seq=testimonial-hero-${story.id}&orientation=landscape`}
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-abyss-900/80" />
          <div className="absolute inset-0 bg-gradient-to-b from-abyss-900/50 via-abyss-900/70 to-abyss-900" />
        </div>

        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="reveal">
            <Link
              to="/testimonials"
              className="inline-flex items-center gap-2 text-ghost-muted text-base hover:text-cyber-gold transition-colors mb-6"
            >
              <i className="ri-arrow-left-line" />
              返回客戶見證
            </Link>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-base font-mono font-medium"
                style={{
                  color: categoryAccent,
                  backgroundColor: `${categoryAccent}15`,
                }}
              >
                <i className={`${categoryIcon} text-base`} />
                {categoryLabel}
              </span>
            </div>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-ghost leading-tight max-w-4xl">
              {story.identity}｜{story.concern}
            </h1>
          </div>
        </div>
      </section>

      {/* Story Content */}
      <section className="py-8 md:py-16">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 800 }}>
          <div className="card-cyber p-8 md:p-12 reveal">
            <div className="space-y-8">
              {/* Before */}
              <div>
                <p className="font-mono text-base mb-2" style={{ color: categoryAccent }}>// BEFORE</p>
                <p className="text-ghost-muted text-base md:text-lg leading-[1.8]">{story.before}</p>
              </div>
              {/* During */}
              <div>
                <p className="font-mono text-base mb-2" style={{ color: categoryAccent }}>// DURING</p>
                <p className="text-ghost-muted text-base md:text-lg leading-[1.8]">{story.during}</p>
              </div>
              {/* Now */}
              <div>
                <p className="font-mono text-base mb-2" style={{ color: categoryAccent }}>// NOW</p>
                <p className="text-ghost text-base md:text-lg leading-[1.8]">{story.now}</p>
              </div>
            </div>

            {/* Disclaimer */}
            <div
              className="mt-10 pt-8 border-t border-abyss-700 px-4 py-3 rounded-xl text-base"
              style={{
                background: `${categoryAccent}08`,
                color: `${categoryAccent}cc`,
                borderTop: `1px solid ${categoryAccent}15`,
              }}
            >
              {story.note}
            </div>
          </div>
        </div>
      </section>

      {/* Prev / Next */}
      <section className="py-8 md:py-12">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 800 }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {prevStory ? (
              <button
                onClick={() => navigate(`/testimonials/${prevStory.id}`)}
                className="card-cyber p-5 md:p-6 text-left hover:border-cyber-gold/40 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-1.5 text-ghost-dim text-base mb-2">
                  <i className="ri-arrow-left-line" />
                  上一篇
                </span>
                <p className="text-ghost font-medium text-base line-clamp-2">{prevStory.title}</p>
              </button>
            ) : (
              <div />
            )}
            {nextStory ? (
              <button
                onClick={() => navigate(`/testimonials/${nextStory.id}`)}
                className="card-cyber p-5 md:p-6 text-right hover:border-cyber-gold/40 transition-all duration-300 cursor-pointer group"
              >
                <span className="flex items-center justify-end gap-1.5 text-ghost-dim text-base mb-2 group-hover:text-cyber-gold transition-colors">
                  下一篇
                  <i className="ri-arrow-right-line" />
                </span>
                <p className="text-ghost font-medium text-base line-clamp-2">{nextStory.title}</p>
              </button>
            ) : (
              <div />
            )}
          </div>
        </div>
      </section>

      {/* Related Testimonials */}
      {displayedRelated.length > 0 && (
        <section className="py-8 md:py-12 border-t border-abyss-800">
          <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 800 }}>
            <p className="font-mono text-base mb-6" style={{ color: categoryAccent }}>
              // RELATED TESTIMONIES
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedRelated.map((t) => (
                <button
                  key={t.id}
                  onClick={() => navigate(`/testimonials/${t.id}`)}
                  className="card-cyber p-5 text-left hover:border-cyber-gold/40 transition-all duration-300 cursor-pointer group"
                >
                  <span
                    className="inline-block px-2 py-0.5 rounded text-sm font-mono mb-3"
                    style={{
                      color: t.categoryAccent,
                      backgroundColor: `${t.categoryAccent}15`,
                    }}
                  >
                    {t.categoryLabel}
                  </span>
                  <p className="text-ghost font-medium text-base line-clamp-2 group-hover:text-cyber-gold transition-colors">
                    {t.title}
                  </p>
                  <p className="text-ghost-dim text-sm mt-2 line-clamp-1">
                    {t.identity}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center reveal">
            <span className="section-label">// INITIATE BOOKING SEQUENCE</span>
            <h2 className="mt-4 text-2xl md:text-4xl font-bold text-ghost">
              讀完故事，想親自評估自己的狀態？
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={lineUrl} target="_blank" rel="noopener noreferrer" className="btn-line whitespace-nowrap">
                <i className="ri-line-fill" />
                加入 LINE 諮詢
              </a>
              <Link to="/booking" className="btn-gold whitespace-nowrap">
                預約逆齡評估
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}